'use client';

import { useMemo, useRef, useState } from 'react';

import {
  AsYouType,
  getCountryCallingCode,
  parsePhoneNumberFromString,
  type CountryCode,
} from 'libphonenumber-js';
import { Loader2 } from 'lucide-react';

import { type SchedulerHandoff } from './meeting-scheduler';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { getAttribution, track } from '@/lib/analytics';
import { emailDomain, isFreeEmailDomain } from '@/lib/demo-lead';
import { useTranslation } from '@/lib/i18n/context';
import { Link } from '@/lib/i18n/link';
import { getCountryFromCookie, type VisitorRegion } from '@/lib/regions';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function splitName(fullName: string): { firstName: string; lastName: string } {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: '', lastName: '' };
  if (parts.length === 1) return { firstName: parts[0], lastName: '' };
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
}

interface DemoFormProps {
  region: VisitorRegion;
  locale: string;
  onSuccess: (handoff: SchedulerHandoff) => void;
}

type Errors = Partial<Record<'name' | 'email' | 'phone', string>>;

const DemoForm = ({ region, locale, onSuccess }: DemoFormProps) => {
  const { t } = useTranslation();
  // `demo_form_start` should mean "someone began filling this in", so it fires
  // on the first real keystroke and never again for this mount. A focus event
  // would over-count: tabbing through the page is not intent.
  const startedRef = useRef(false);
  const markStarted = () => {
    if (startedRef.current) return;
    startedRef.current = true;
    track('demo_form_start', { locale, visitor_region: region });
  };
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  // Geo cookie → country applied silently; guard against codes libphonenumber
  // doesn't know (e.g. XK).
  const [detectedCountry] = useState<CountryCode | null>(() => {
    const c = getCountryFromCookie() as CountryCode | null;
    if (!c) return null;
    try {
      getCountryCallingCode(c);
      return c;
    } catch {
      return null;
    }
  });
  // Self-contained phone field (no react-phone-number-input — its bare input
  // spams a console error with defaultCountry-but-no-country). The dial code
  // is pre-filled ("+966 …") so the visitor just types their number; a leading
  // "+xx" always wins, so they can switch country by retyping the prefix. We
  // store the raw formatted string and derive E.164 on submit.
  const [phone, setPhone] = useState<string>(() =>
    detectedCountry ? `+${getCountryCallingCode(detectedCountry)} ` : '+',
  );
  const phoneE164 = useMemo(() => {
    const parsed = parsePhoneNumberFromString(
      phone,
      detectedCountry ?? undefined,
    );
    return parsed?.isValid() ? parsed.number : undefined;
  }, [phone, detectedCountry]);

  const handlePhoneChange = (raw: string) => {
    // AsYouType formats as they type; a leading "+" formats international
    // (and re-detects country), otherwise it uses the detected country.
    const formatter = new AsYouType(
      raw.trim().startsWith('+') ? undefined : (detectedCountry ?? undefined),
    );
    setPhone(formatter.input(raw));
  };
  // Honeypot — hidden from real users; bots that fill it are silently dropped.
  const [companyWebsite, setCompanyWebsite] = useState('');

  const [errors, setErrors] = useState<Errors>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const validate = (): Errors => {
    const next: Errors = {};
    if (!name.trim()) next.name = t.demo['Please enter your name.'];
    const trimmedEmail = email.trim();
    if (!trimmedEmail || !EMAIL_REGEX.test(trimmedEmail)) {
      next.email = t.demo['Please enter a valid work email.'];
    } else {
      const domain = emailDomain(trimmedEmail);
      if (domain && isFreeEmailDomain(domain)) {
        next.email =
          t.demo['Please use your work email, not a personal address.'];
      }
    }
    if (!phoneE164) next.phone = t.demo['Please enter a valid phone number.'];
    return next;
  };

  const handleSubmit = async () => {
    // Guard against double-fire (Enter key bypasses the button's disabled).
    if (submitting) return;
    setSubmitError(null);
    const next = validate();
    setErrors(next);
    if (Object.keys(next).length > 0) {
      // Which field blocks people is the first thing you want when the
      // completion rate is bad, and it is invisible server-side.
      track('demo_form_invalid', {
        fields: Object.keys(next).join(','),
        locale,
      });
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch('/api/demo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          phone: phoneE164,
          visitorRegion: region,
          visitorLanguage: locale,
          company_website: companyWebsite,
          attribution: getAttribution(),
        }),
      });

      if (!res.ok) {
        setSubmitting(false);
        track('demo_form_error', { reason: String(res.status), locale });
        setSubmitError(
          res.status === 429
            ? t.demo[
                'Too many attempts — please wait a few minutes and try again.'
              ]
            : t.demo[
                'Something went wrong submitting the form. Please try again.'
              ],
        );
        return;
      }

      const data = (await res.json()) as {
        meetingUrl?: string;
        ownerKey?: SchedulerHandoff['ownerKey'];
        ownerFirstName?: string;
      };
      const { firstName, lastName } = splitName(name);
      // GA4's standard lead event name, so it can be marked a key event and
      // imported into Google Ads without a custom conversion.
      track('generate_lead', {
        locale,
        visitor_region: region,
        routed_owner: data.ownerKey ?? null,
        ...getAttribution(),
      });
      onSuccess({
        contact: { firstName, lastName, email: email.trim() },
        ownerKey: data.ownerKey ?? null,
        ownerFirstName: data.ownerFirstName ?? null,
        phone: phoneE164 ?? '',
        meetingUrl: data.meetingUrl ?? null,
      });
    } catch {
      setSubmitting(false);
      track('demo_form_error', { reason: 'network', locale });
      setSubmitError(
        t.demo['Network error. Please check your connection and try again.'],
      );
    }
  };

  const fieldError = (key: keyof Errors) =>
    errors[key] ? (
      <p className="mt-1.5 text-xs text-red-500">{errors[key]}</p>
    ) : null;

  const inputInvalid = (key: keyof Errors) =>
    errors[key] ? 'border-red-400 focus-visible:ring-red-400' : '';

  return (
    // Intentionally NOT a <form>: the site-wide HubSpot tracking script includes
    // "Collected Forms", which auto-captures any native form `submit` event and
    // logs a junk duplicate conversion (named after the form's CSS classes). We
    // submit exclusively through our own /api/demo → Submissions API path, so we
    // avoid emitting a submit event entirely and keep Enter-to-submit manually.
    <div
      role="form"
      aria-label={locale === 'ar' ? 'احجز عرضاً توضيحياً' : 'Book a demo'}
      onKeyDown={(e) => {
        if (
          e.key === 'Enter' &&
          (e.target as HTMLElement).tagName === 'INPUT'
        ) {
          e.preventDefault();
          void handleSubmit();
        }
      }}
      className="space-y-5 p-6 sm:p-7"
    >
      {/* Name */}
      <div>
        <Label htmlFor="demo-name" className="mb-1.5 block text-sm font-medium">
          {t.demo.Name} <span className="text-red-500">*</span>
        </Label>
        <Input
          id="demo-name"
          value={name}
          onChange={(e) => {
            markStarted();
            setName(e.target.value);
          }}
          placeholder={t.demo['Jane Doe']}
          autoComplete="name"
          className={inputInvalid('name')}
        />
        {fieldError('name')}
      </div>

      {/* Email */}
      <div>
        <Label
          htmlFor="demo-email"
          className="mb-1.5 block text-sm font-medium"
        >
          {t.demo['Work email']} <span className="text-red-500">*</span>
        </Label>
        <Input
          id="demo-email"
          type="email"
          value={email}
          onChange={(e) => {
            markStarted();
            setEmail(e.target.value);
          }}
          placeholder="jane@company.com"
          autoComplete="email"
          className={inputInvalid('email')}
        />
        {fieldError('email')}
      </div>

      {/* Phone — no country selector: the dial code comes pre-filled from the
          geo cookie and formats as you type; a "+xx" prefix switches country.
          Plain input + libphonenumber formatting (no third-party widget). */}
      <div>
        <Label
          htmlFor="demo-phone"
          className="mb-1.5 block text-sm font-medium"
        >
          {t.demo['Phone number']} <span className="text-red-500">*</span>
        </Label>
        <Input
          id="demo-phone"
          type="tel"
          inputMode="tel"
          dir="ltr"
          autoComplete="tel"
          value={phone}
          onChange={(e) => {
            markStarted();
            handlePhoneChange(e.target.value);
          }}
          className={inputInvalid('phone')}
        />
        {fieldError('phone')}
        <p className="text-muted-foreground mt-1.5 text-xs">
          {t.demo['smsConsent']}{' '}
          <a href="/terms" className="underline">
            {t.demo['smsConsentTerms']}
          </a>{' '}
          <a href="/privacy-policy" className="underline">
            {t.demo['smsConsentPrivacy']}
          </a>
        </p>
      </div>

      {/* Honeypot: visually hidden, off-screen, not tab-reachable. */}
      <div
        aria-hidden
        className="pointer-events-none absolute -left-[9999px] opacity-0"
      >
        <label htmlFor="company_website">Company website (leave blank)</label>
        <input
          id="company_website"
          name="company_website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={companyWebsite}
          onChange={(e) => setCompanyWebsite(e.target.value)}
        />
      </div>

      {submitError && (
        <p className="text-sm text-red-500" role="alert">
          {submitError}
        </p>
      )}

      <div className="space-y-3 pt-1">
        <Button
          type="button"
          onClick={() => void handleSubmit()}
          size="lg"
          disabled={submitting}
          className="w-full"
        >
          {submitting ? (
            <>
              <Loader2 className="size-4 animate-spin" />
              {t.demo['Submitting...']}
            </>
          ) : (
            t.demo['Find a time to meet']
          )}
        </Button>
        <p className="text-muted-foreground text-center text-xs leading-relaxed">
          {
            t.demo[
              'For information on how to unsubscribe, and our privacy practices, please review our'
            ]
          }{' '}
          <Link
            href="/privacy-policy"
            className="underline hover:text-foreground"
          >
            {t.demo['Privacy Policy']}
          </Link>
          .
        </p>
      </div>
    </div>
  );
};

export default DemoForm;
